/**
 * Store-update manifest — tiered nudges toward App Store / Play Store updates.
 *
 * Distinct from the OTA release manifest (`release.ts`). The OTA path force-applies JS bundles
 * silently; a store update requires the user to leave the app and tap Update on the store
 * listing, so it needs UI. This file describes the shape the client fetches from R2 and the
 * tier logic that decides what UI to render.
 *
 * NATIVE-ONLY. The web build never renders any of this — there is no store to point at.
 *
 * The manifest is static JSON at `${STORE_UPDATE_MANIFEST_URL}` (R2). Admin edits go through
 * `PUT /api/admin/store-updates`, which is the only writer. Reads are anonymous.
 *
 * Backend mirror: `api_backend_fastify_server/src/types/store-update.types.ts` — mirrored
 * rather than imported so the API's tsconfig does not drag the frontend into its build tree
 * (same reason `release.ts` is mirrored). Keep the two in step.
 */

import { compareVersions } from './release';

export const STORE_UPDATE_PLATFORMS = ['ios', 'android'] as const;
export type StoreUpdatePlatform = (typeof STORE_UPDATE_PLATFORMS)[number];

/**
 * Per-platform update policy. All version fields are SemVer MAJOR.MINOR.PATCH.
 *
 * Tier rules, evaluated in order:
 *   running < minMandatoryVersion   → 'mandatory' (blocking modal)
 *   running < minRecommendedVersion → 'recommended' (sticky non-dismissible banner)
 *   running < latestVersion         → 'optional' (dismissible banner, once per version)
 *   otherwise                       → 'none'
 *
 * ORDERING INVARIANT: minMandatory ≤ minRecommended ≤ latestVersion. The admin write path
 * enforces this; the client tolerates violations by evaluating in the order above (mandatory
 * always wins), so a misordered file is a suboptimal nudge, never a broken app.
 */
export interface StoreUpdatePlatformPolicy {
  latestVersion: string;
  minRecommendedVersion: string;
  minMandatoryVersion: string;
  storeUrl: string;
}

export interface StoreUpdateManifest {
  ios?: StoreUpdatePlatformPolicy;
  android?: StoreUpdatePlatformPolicy;
  /** ISO 8601 UTC. Written by the server on every admin PUT; the client only displays it. */
  updatedAt?: string;
  /** Auth user id of the last admin editor. Server-side stamp; UI-only. */
  updatedBy?: string;
}

export const STORE_UPDATE_TIERS = ['none', 'optional', 'recommended', 'mandatory'] as const;
export type StoreUpdateTier = (typeof STORE_UPDATE_TIERS)[number];

/**
 * Decide which tier applies to a running version against a platform policy.
 *
 * Returns `'none'` when the input is missing (no policy for this platform, no running version,
 * unparseable version). The client's rule is "when in doubt, stay silent" — an unparseable
 * comparison must never surface a modal.
 */
export function resolveTier(
  runningVersion: string | null | undefined,
  policy: StoreUpdatePlatformPolicy | undefined
): StoreUpdateTier {
  if (!policy || !runningVersion) return 'none';

  const belowMandatory = compareVersions(runningVersion, policy.minMandatoryVersion);
  if (belowMandatory === -1) return 'mandatory';

  const belowRecommended = compareVersions(runningVersion, policy.minRecommendedVersion);
  if (belowRecommended === -1) return 'recommended';

  const belowLatest = compareVersions(runningVersion, policy.latestVersion);
  if (belowLatest === -1) return 'optional';

  return 'none';
}

/** Structural check for a manifest fetched from R2. Additive — unknown keys are tolerated. */
export function isStoreUpdateManifest(value: unknown): value is StoreUpdateManifest {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  for (const platform of STORE_UPDATE_PLATFORMS) {
    const entry = record[platform];
    if (entry === undefined) continue;
    if (!isPlatformPolicy(entry)) return false;
  }
  return true;
}

function isPlatformPolicy(value: unknown): value is StoreUpdatePlatformPolicy {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.latestVersion === 'string' &&
    typeof record.minRecommendedVersion === 'string' &&
    typeof record.minMandatoryVersion === 'string' &&
    typeof record.storeUrl === 'string'
  );
}
